import React, { useState } from 'react';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import fr from 'facet-rest';
import { useNavigationContext } from 'Components/ComponentLibrary/Organisms/NavigationWithContext/NavigationContext';
import useIsDigitalOnboarding from 'Hooks/useIsDigitalOnboarding';
import usePageHandleRedirectOnClose from 'Views/Page/Hooks/usePageHandleRedirectOnClose';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import PropTypes from 'prop-types';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import classNames from 'classnames';
import WizardStandardHeader from 'Components/ComponentLibrary/Components/Uncategorized/WizardStandardHeader';

const useStyles = buildUseStyles('UseWizardStepWrapperTemplate', theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100dvh',
  },
  fixedRoot: {
    minHeight: 'unset',
  },
  desktopHeader: {
    marginBottom: theme.spacing(4),
  },
  mobileHeader: {
    marginBottom: theme.spacing(2),
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: '1',
    alignItems: 'center',
    padding: 0,
  },
  navigation: {
    position: 'sticky',
    bottom: 0,
    width: '100%',
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    zIndex: 1,
    flexDirection: 'column',
  },
  fixedNavigation: {
    position: 'fixed',
  },
  previousButton: {},
  continueButton: {
    margin: theme.spacing(0, 1),
  },
  errorList: {
    borderRadius: theme.radius.md,
    maxWidth: theme.dimensions.lg,
    margin: theme.spacing(2),
  },
}));

export function useWizardStepWrapperTemplate({
  resource,
  template,
  onNext,
  onClose,
  hideBack,
  onSpecificLinkClicked,
  disableNextButton,
  submitConfirmation,
  headerTextColor,
  showMobileSideNavigationMenu,
  headerTransparentBackground,
}) {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const { logStandardizedEvent, logStandardizedEventFailureWithError } = useEventTrackingLogEvent();
  const { loadPageContentLink, setPageContentCollection, isPageContentLoading } = usePageContentContext();
  const previousLink = !hideBack ? resource.relationship(fr.relationships.previousLink.name)?.link('related') : null;
  const templateName = template || resource.type();
  const canPost = resource.meta().action('POST') || onNext;
  const shouldPostWithConfirmation = resource.meta().action('POST') && submitConfirmation;
  const { navigationCollection } = useNavigationContext();
  const isDigitalOnboarding = useIsDigitalOnboarding();

  const { hasRelationshipRedirectOnClose, handleRedirectOnClose } = usePageHandleRedirectOnClose();
  let handleHeaderClose = null;
  if (onClose) {
    handleHeaderClose = onClose;
  } else if (hasRelationshipRedirectOnClose) {
    handleHeaderClose = handleRedirectOnClose;
  }

  const handleNext = async () => {
    logStandardizedEvent(categories.wizard, eventTypes.click, 'continue', { eventProperties: { templateName } });
    setErrors(null);
    setLoading(true);
    try {
      if (onNext) {
        await onNext();
      } else {
        const updatedColl = await resource.submit();
        await setPageContentCollection(updatedColl);
      }
    } catch (e) {
      logStandardizedEventFailureWithError(categories.wizard, eventTypes.click, 'continue', e, { eventProperties: { templateName } });
      setErrors(e.errors);
    } finally {
      setLoading(false);
    }
  };

  const {
    dialogOpen,
    handleSubmit,
    handleOpen,
    handleClose,
  } = useConfirmationDialogState({
    onSubmit: handleNext,
    closeOnSubmit: true,
  });

  const onErrorLinkClick = async (errorLink) => {
    const link = fr.link({ data: { url: errorLink } });
    const updatedColl = await link.get();
    await setPageContentCollection(updatedColl);
  };

  const onPrevious = previousLink ? (
    async () => {
      logStandardizedEvent(categories.wizard, eventTypes.click, 'back', { eventProperties: { templateName } });
      setLoading(true);
      setErrors(null);
      try {
        await loadPageContentLink(previousLink);
      } catch (e) {
        logStandardizedEventFailureWithError(categories.wizard, eventTypes.click, 'back', e, { eventProperties: { templateName } });
        setLoading(false);
        setErrors(e.errors);
      }
      setLoading(false);
    }
  ) : null;

  const handlePageBottomLinkClick = async () => {
    logStandardizedEvent(categories.wizard, eventTypes.click, 'specific page link', {
      eventProperties: {
        templateName,
        prompt: resource?.relationship(fr.relationships.specificPageLink.name)?.meta('prompt'),
      },
    });
    setErrors(null);
    setLoading(true);
    try {
      if (onSpecificLinkClicked) {
        return await onSpecificLinkClicked();
      }
      if (resource.relationship(fr.relationships.specificPageLink.name).meta('submitResourceBefore') === true) {
        await resource.submit();
      }
      setPageContentCollection(await resource.relationship(fr.relationships.specificPageLink.name).link('related').get());
    } catch (e) {
      logStandardizedEventFailureWithError(categories.wizard, eventTypes.click, 'specific page link', e, {
        eventProperties: {
          templateName,
          prompt: resource?.relationship(fr.relationships.specificPageLink.name)?.meta('prompt'),
        },
      });
      setErrors(e.errors);
    } finally {
      setLoading(false);
    }
    return null;
  };

  const handleOnNext = () => {
    if (disableNextButton) {
      return undefined;
    }
    if (!canPost && !shouldPostWithConfirmation) {
      return undefined;
    }

    if (shouldPostWithConfirmation) {
      return handleOpen;
    }

    return handleNext;
  };

  const classes = useStyles();

  const header = (
    <WizardStandardHeader
      color={headerTextColor}
      title={resource.attr('moduleName')}
      navigationExists={!!navigationCollection}
      showMobileSideNavigationMenu={showMobileSideNavigationMenu}
      transparentBackground={headerTransparentBackground}
      onClose={handleHeaderClose}
    />
  );

  return {
    classes: {
      ...classes,
      root: classNames(classes.root, {
        [classes.fixedRoot]: isDigitalOnboarding,
      }),
      navigation: classNames(classes.navigation, {
        [classes.fixedNavigation]: isDigitalOnboarding,
      }),
    },
    header,
    handleOnNext,
    handlePageBottomLinkClick,
    onPrevious,
    onErrorLinkClick,
    dialogOpen,
    handleSubmit,
    handleClose,
    errors,
    loading,
    isPageContentLoading,
  };
}

useWizardStepWrapperTemplate.defaultProps = {
  template: '',
  onNext: undefined,
  onClose: undefined,
  hideBack: false,
  onSpecificLinkClicked: undefined,
  submitConfirmation: false,
  disableNextButton: false,
};

useWizardStepWrapperTemplate.propTypes = {
  /** Template is used for Amplitude logging */
  template: PropTypes.string,
  /** resource: The displayed resource * */
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
  onNext: PropTypes.func,
  onClose: PropTypes.func,
  hideBack: PropTypes.bool,
  onSpecificLinkClicked: PropTypes.func,
  submitConfirmation: PropTypes.bool,
  disableNextButton: PropTypes.bool,
};
